import api from './apiClient';
import authService from './authService';
import type { RootEntityType } from '../types/rootEntity';
import type {
  Tenant,
  TenantWithChildren,
  TenantWithCounts,
  CallerTenant,
  TenantCounts,
} from '../types/entities';

// Re-exports so consumers can keep importing from this module if they like.
export type { Tenant, TenantWithChildren, TenantWithCounts, CallerTenant, TenantCounts };

/**
 * @deprecated Use `TenantWithChildren` from src/types/entities.ts instead.
 * Kept as an alias so existing imports keep working.
 */
export type TenantTreeNode = TenantWithChildren;

/**
 * Shape returned by GET /tenant/me — the effective tenant for the request.
 *
 * @deprecated Use `CallerTenant` from src/types/entities.ts. Kept as a
 * type alias so existing imports keep working.
 */
export type MyTenant = CallerTenant;

export interface AvailableStandard {
  id: string;
  name: string;
  code: string | null;
}

export async function listAvailableStandards(): Promise<AvailableStandard[]> {
  return api.get<AvailableStandard[]>('/tenant/standards/available');
}

export interface MyStandardRow {
  tenantStandardId: string;
  controlReferenceId: string;
  name: string;
  code: string | null;
  publishedVersion: number;
  lastPublishedAt: string | null;
  adoptedVersion: number;
  adoptedAt: string | null;
  isActive: boolean;
}

export async function listMyStandards(): Promise<MyStandardRow[]> {
  return api.get<MyStandardRow[]>('/tenant/my-standards');
}

export async function publishStandard(
  controlReferenceId: string,
): Promise<{ publishedVersion: number; lastPublishedAt: string }> {
  return api.post(`/tenant/standards/${encodeURIComponent(controlReferenceId)}/publish`);
}

export async function acceptStandardUpdate(
  tenantStandardId: string,
): Promise<{ adoptedVersion: number }> {
  return api.post(
    `/tenant/my-standards/${encodeURIComponent(tenantStandardId)}/accept-update`,
  );
}

export async function uploadTenantLogo(
  tenantId: string,
  file: File,
): Promise<{ logoUrl: string }> {
  const form = new FormData();
  form.append('logo', file);
  // Multipart upload — apiClient handles FormData and unwraps the envelope.
  // We use authService directly here because we want to preserve the error
  // message shape from the legacy implementation.
  const response = await authService.apiCall(
    `${api.getBaseUrl()}/tenant/${encodeURIComponent(tenantId)}/logo`,
    { method: 'POST', body: form },
  );
  const data = await response.json();
  if (!response.ok || data.success === false) {
    throw new Error(data?.message || 'Logo upload failed');
  }
  return data.data;
}

export async function getTenantModules(tenantId: string): Promise<string[]> {
  return api.get<string[]>(`/tenant/${encodeURIComponent(tenantId)}/modules`);
}

export async function setTenantModules(
  tenantId: string,
  disabledModules: string[],
): Promise<{ updated: boolean; disabledModules: string[] }> {
  return api.put(`/tenant/${encodeURIComponent(tenantId)}/modules`, { disabledModules });
}

/**
 * Returns the subtree rooted at the current tenant (own + descendants),
 * already shaped as a nested tree. The backend returns a flat list ordered
 * by depth/path; this helper builds the parent-child structure.
 */
export async function getTenantTree(rootId?: string): Promise<TenantTreeNode[]> {
  const query = rootId ? `?rootId=${encodeURIComponent(rootId)}` : '';
  const flat = await api.get<TenantTreeNode[]>(`/tenant/tree${query}`);
  const byId = new Map<string, TenantTreeNode>(flat.map((n) => [n.id, { ...n, children: [] }]));
  const roots: TenantTreeNode[] = [];
  for (const node of byId.values()) {
    if (node.parentTenantId && byId.has(node.parentTenantId)) {
      byId.get(node.parentTenantId)!.children!.push(node);
    } else {
      roots.push(node);
    }
  }
  return roots;
}

export async function createChildTenant(
  parentId: string,
  body: {
    name: string;
    nameAr?: string;
    slug?: string;
    logoUrl?: string;
    type?: 'group' | 'leaf';
    industry?: string;
    contactInfo?: string;
    standardIds?: string[];
    admin?: { username: string; email?: string; name?: string };
  },
): Promise<TenantTreeNode> {
  return api.post<TenantTreeNode>(`/tenant/${encodeURIComponent(parentId)}/children`, body);
}

/** Returns the *effective* tenant for the current request (reflects view-as).
 *
 *  The backend's canonical field is `archetype`. For one release we also
 *  populate the deprecated `rootEntityType` so existing call sites keep
 *  working unchanged. New code should read `archetype`. */
export async function getMyTenant(): Promise<MyTenant> {
  const raw = await api.get<MyTenant & { archetype?: RootEntityType | null }>('/tenant/me');
  const archetype = raw.archetype ?? raw.rootEntityType ?? null;
  return { ...raw, archetype, rootEntityType: archetype };
}

export async function moveTenant(
  tenantId: string,
  newParentId: string,
): Promise<TenantTreeNode> {
  return api.put<TenantTreeNode>(`/tenant/${encodeURIComponent(tenantId)}/move`, { newParentId });
}

/**
 * The /tenant/:id/snapshot endpoint returns a tenant entity *with* counts.
 * The canonical shape is `TenantWithCounts`, but this endpoint splits the
 * tenant fields and counts into two top-level keys for historical reasons.
 * Both shapes are equivalent in content.
 */
export interface TenantSnapshot {
  tenant: Omit<TenantWithCounts, 'counts'>;
  counts: TenantCounts;
}

export async function getTenantSnapshot(tenantId: string): Promise<TenantSnapshot> {
  return api.get<TenantSnapshot>(`/tenant/${encodeURIComponent(tenantId)}/snapshot`);
}

export async function updateTenant(
  tenantId: string,
  updates: {
    name?: string;
    industry?: string;
    contactInfo?: string;
    isActive?: boolean;
    rootEntityType?: RootEntityType;
  },
): Promise<{ updated: boolean }> {
  return api.put(`/tenant/${encodeURIComponent(tenantId)}`, updates);
}
